/* global __APP_VERSION__ */
import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';

// Baked in at build time (see vite.config.js). The deployed /version.json is
// regenerated on every build, so when it stops matching this value the browser
// is running an old bundle and a new one is live.
const CURRENT_VERSION = __APP_VERSION__;
const POLL_MS = 60000;

async function fetchDeployedVersion() {
  const res = await fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) throw new Error('version.json not available');
  const data = await res.json();
  return data.version;
}

// Users only pick up a new deploy after a *hard* refresh because a cached
// index.html keeps pointing at the old hashed bundles. Clear any caches and
// request a fresh document (a new URL is never served from the HTTP cache).
async function hardRefresh() {
  try {
    if (window.caches) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // ignore — reloading still helps
  }
  const url = new URL(window.location.href);
  url.searchParams.set('_', Date.now().toString());
  window.location.replace(url.toString());
}

const UpdateNotifier = () => {
  const [available, setAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const stopped = useRef(false);

  useEffect(() => {
    let timer;

    const check = async () => {
      if (stopped.current) return;
      try {
        const deployed = await fetchDeployedVersion();
        if (deployed && deployed !== CURRENT_VERSION) {
          stopped.current = true; // found it — stop polling
          setAvailable(true);
          return;
        }
      } catch {
        // dev server (no version.json) or offline — just try again later
      }
      timer = setTimeout(check, POLL_MS);
    };

    // Small delay so we don't race the very deploy that just served this page.
    timer = setTimeout(check, 5000);

    // Also check the moment the user returns to the tab.
    const onFocus = () => {
      if (!stopped.current && document.visibilityState !== 'hidden') check();
    };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  if (!available || dismissed) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[9999] w-full max-w-sm pointer-events-auto">
      <div className="rounded-lg border border-blue-200 bg-blue-50 shadow-lg ring-1 ring-black ring-opacity-5 p-4">
        <div className="flex items-start gap-3">
          <RefreshCw className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-800">A new version is available</p>
            <p className="text-sm text-blue-700 mt-0.5">
              Refresh to get the latest updates and fixes.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={hardRefresh}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="h-4 w-4" /> Refresh now
              </button>
              <button
                onClick={() => setDismissed(true)}
                className="px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-100 rounded-lg transition-colors"
              >
                Later
              </button>
            </div>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="text-blue-400 hover:text-blue-600"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateNotifier;
